/**
 * Selector entre la tabla del portfolio y la brecha de activación.
 *
 * Es un `tablist` de verdad (flechas, `aria-selected`, `aria-controls`) y no dos
 * botones que parecen tabs: la vista que está activa la tiene que poder saber un
 * lector de pantalla, no solo el fondo verde.
 *
 * (Carencia anotada del design system: no hay `Tabs` ni control segmentado, así
 * que el markup vive acá y no en `src/ui` — otros cinco módulos están tocando esa
 * carpeta en paralelo.)
 */
import { useRef, type KeyboardEvent } from 'react'

import { Icon, type IconName } from '../../ui'

export type Vista = 'portfolio' | 'brecha'

export interface OpcionVista {
  vista: Vista
  label: string
  icono: IconName
  /** Conteo al lado del label. `null` = sin badge. */
  cantidad: number | null
  /**
   * De dónde sale el conteo, en el formato del badge de proveniencia:
   * `plataforma · cuentas del portfolio de julio 2026`.
   *
   * Es obligatoria y no opcional porque la regla 1 no admite excepciones: cada
   * cifra del panel lleva su fuente. Estos conteos coinciden hoy con los de las
   * tarjetas de arriba, pero un número sin fuente enseña que hay números sin
   * fuente. Un `ProvenanceBadge` entero no entra en un tab de 24px de alto, así
   * que la fuente va como nombre accesible del conteo y como tooltip.
   */
  proveniencia: string
}

export interface SelectorVistaProps {
  vista: Vista
  onChange: (vista: Vista) => void
  opciones: readonly OpcionVista[]
}

export function idPanelVista(vista: Vista): string {
  return `panel-vista-${vista}`
}

export function SelectorVista({ vista, onChange, opciones }: SelectorVistaProps) {
  const refs = useRef(new Map<Vista, HTMLButtonElement>())

  function onKeyDown(evento: KeyboardEvent<HTMLDivElement>) {
    if (evento.key !== 'ArrowRight' && evento.key !== 'ArrowLeft') return
    const indice = opciones.findIndex((opcion) => opcion.vista === vista)
    if (indice === -1) return
    const paso = evento.key === 'ArrowRight' ? 1 : opciones.length - 1
    const siguiente = opciones[(indice + paso) % opciones.length]
    if (siguiente === undefined) return
    evento.preventDefault()
    onChange(siguiente.vista)
    refs.current.get(siguiente.vista)?.focus()
  }

  return (
    <div
      role="tablist"
      aria-label="Vista del módulo de clientes"
      onKeyDown={onKeyDown}
      className="inline-flex w-max gap-0.5 rounded-card border border-border bg-surface p-1"
    >
      {opciones.map((opcion) => {
        const activa = opcion.vista === vista
        return (
          <button
            key={opcion.vista}
            ref={(nodo) => {
              if (nodo !== null) refs.current.set(opcion.vista, nodo)
            }}
            type="button"
            role="tab"
            id={`tab-${opcion.vista}`}
            aria-selected={activa}
            aria-controls={idPanelVista(opcion.vista)}
            tabIndex={activa ? 0 : -1}
            onClick={() => onChange(opcion.vista)}
            className={
              activa
                ? 'inline-flex items-center gap-1.5 rounded-md bg-accent-soft px-3 py-1 text-meta font-semibold text-accent-text'
                : 'inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-meta font-semibold text-muted-strong transition-colors duration-150 ease-brand hover:bg-n100 hover:text-text'
            }
          >
            <Icon name={opcion.icono} size={13} />
            {opcion.label}
            {opcion.cantidad !== null ? (
              <span
                data-testid="tab-conteo"
                title={opcion.proveniencia}
                className="tabular rounded-full border border-border bg-surface px-1.5 text-micro font-semibold text-muted-strong"
              >
                {opcion.cantidad}
                <span className="sr-only"> — {opcion.proveniencia}</span>
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
